import { computed, ErrorHandler, Injectable, inject, type Signal } from '@angular/core';
import { buildCategoryNodeMap } from '../category/category-node-map';
import type { PortalCategory } from '../category/portal-category';
import { loadOnceResource } from '../shared/load-once-resource';
import { CatalogRepository } from './catalog-repository';

/** Stable empty taxonomy, so the categories signal does not churn before the load lands. */
const NO_CATEGORIES: readonly PortalCategory[] = [];

/**
 * The portal's global reference data. Its first dataset is the category
 * taxonomy, which every consumer reads through the node map derived here.
 *
 * Cache semantics for static reference data: lazy on the first consumer, one read per
 * session, no revalidation.
 */
@Injectable()
export class CatalogStore {
  private readonly repository = inject(CatalogRepository);
  private readonly errorHandler = inject(ErrorHandler);

  // Reporting from the read itself, so concurrent callers of one read see one report.
  private readonly loaded = loadOnceResource(async () => {
    try {
      return await this.repository.listCategories();
    } catch (cause: unknown) {
      this.errorHandler.handleError(cause);
      throw cause;
    }
  });

  readonly categories = computed<readonly PortalCategory[]>(
    () => this.loaded.value() ?? NO_CATEGORIES
  );
  readonly categoryNodeMap = computed(() => buildCategoryNodeMap(this.categories()));

  /**
   * Whether the last taxonomy read failed, for consumers that must say so rather than
   * present an empty taxonomy as a loaded one (HC-04). Cleared when a retry starts.
   */
  readonly taxonomyFailed: Signal<boolean> = this.loaded.failed;

  /**
   * Loads the taxonomy the first time anyone asks, and answers from memory afterwards.
   *
   * A failed read is reported once and leaves the categories empty -- never an empty
   * success -- and is read again on the next call, so a later caller can still succeed.
   */
  async ensureLoaded(): Promise<void> {
    try {
      await this.loaded.load();
    } catch {
      // Reported by the read itself; callers read `taxonomyFailed` for the state it left behind.
    }
  }
}
